import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { schedulesVerificationService } from '@/services/schedulesVerification.service';
import {
  VERIFICATION_PROGRESS_EVENT,
  LORA_VERIFY_POLL_MS,
  listActiveVerificationSessions,
  updateVerificationSessionProgress,
  clearVerificationSession,
  isTransitionalSummary,
  type VerificationProgressDetail,
} from '@/utils/verificationSession';

/**
 * En listado Controllers (u otra página): sondea sesiones LoRa activas
 * aunque el diálogo esté cerrado, y muestra toast al avanzar o completar.
 */
export function useVerificationProgressToasts(enabled = true) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const lastToastKey = useRef<string>('');

  useEffect(() => {
    if (!enabled) return;

    const onProgress = (event: Event) => {
      const detail = (event as CustomEvent<VerificationProgressDetail>).detail;
      if (!detail?.progressed && !detail?.completed) return;

      const key = `${detail.controllerId}:${detail.resolved}:${detail.completed}`;
      if (key === lastToastKey.current) return;
      lastToastKey.current = key;

      const name = detail.controllerName || 'Controladora';
      if (detail.completed) {
        toast({
          title: 'Verificación actualizada',
          description: `${name}: contraste finalizado (${detail.verified}/${detail.total} verificadas). Abra Programaciones para el detalle.`,
        });
      } else {
        toast({
          title: 'Progreso de verificación',
          description: `${name}: ${detail.resolved}/${detail.total} contrastadas · quedan ${detail.pending} en cola LoRa.`,
        });
      }

      void queryClient.invalidateQueries({
        queryKey: ['schedule-verification', detail.controllerId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['local-schedules', detail.controllerId],
      });
    };

    window.addEventListener(VERIFICATION_PROGRESS_EVENT, onProgress);
    return () => window.removeEventListener(VERIFICATION_PROGRESS_EVENT, onProgress);
  }, [enabled, toast, queryClient]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const tick = async () => {
      const sessions = listActiveVerificationSessions();
      if (sessions.length === 0) return;

      await Promise.all(
        sessions.map(async (session) => {
          try {
            const res = await schedulesVerificationService.getStatus(
              session.controllerId
            );
            if (!res.success || !res.data || cancelled) return;

            updateVerificationSessionProgress(session.controllerId, res.data.summary);

            queryClient.setQueryData(['schedule-verification', session.controllerId], {
              status: 'ready',
              data: res.data,
            });

            if (!isTransitionalSummary(res.data.summary)) {
              clearVerificationSession(session.controllerId);
            }
          } catch {
            // soft ignore
          }
        })
      );
    };

    void tick();
    const id = window.setInterval(() => void tick(), LORA_VERIFY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, queryClient]);
}
